import React, { useEffect, useMemo } from 'react';
import * as ReactIntl from 'react-intl';
import { Services } from 'services';
import { Store } from 'store';
import { fallbackLocale, SupportedCountries } from 'global/supportedCountries';
import { changeUrlLocale, getUrlLocale } from 'utils/routing';

const RTL_LANGUAGES = ['ar', 'he'];

const isRTL = (locale: string): boolean => {
  return RTL_LANGUAGES.some((rtlLang) => locale.startsWith(rtlLang));
};

export const IntlProvider: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const { translations } = Services.Translations.useGetTranslations();

  const { user } = Store.useUser();

  const userLocale = useMemo(() => {
    const defaultLocale = getUrlLocale() || fallbackLocale;
    if (user?.languageCode) {
      return (
        SupportedCountries.find((locale) =>
          locale.includes(user.languageCode || '')
        ) || defaultLocale
      );
    }

    return defaultLocale;
  }, [user?.languageCode]);

  useEffect(() => {
    const currentLocale = getUrlLocale();
    if (currentLocale && currentLocale !== userLocale) {
      changeUrlLocale(userLocale);
    }
  }, [userLocale]);

  useEffect(() => {
    document.documentElement.lang = userLocale;
    // Set text direction based on locale (RTL for Arabic, Hebrew, etc.)
    document.documentElement.dir = isRTL(userLocale) ? 'rtl' : 'ltr';
  }, [userLocale]);

  return (
    <ReactIntl.IntlProvider
      locale={userLocale}
      messages={translations ? translations[userLocale] : {}}
      onError={(err) => {
        // For Xhosa number-format throws MISSING_DATA error, fallback to en
        if (err.code === 'MISSING_TRANSLATION' || err.code === 'MISSING_DATA') {
          return;
        }
        throw err;
      }}
    >
      {children}
    </ReactIntl.IntlProvider>
  );
};
